import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { MapPin, RefreshCw, Clock, User, Navigation } from 'lucide-react';
import AdminLayout from '@/components/admin/AdminLayout';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_BACKEND_URL;

// Fix leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const createTeamIcon = (status) => {
  const color = status === 'en_intervention' ? '#dc2626' : status === 'en_route' ? '#2563eb' : '#16a34a';
  return L.divIcon({
    className: 'custom-team-marker',
    html: `<div style="background:${color};width:32px;height:32px;border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M12 2a5 5 0 1 0 0 10 5 5 0 0 0 0-10zm0 12c-5.33 0-8 2.67-8 4v2h16v-2c0-1.33-2.67-4-8-4z"/></svg>
    </div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });
};

const FitBounds = ({ locations }) => {
  const { t } = useTranslation();
  const map = useMap();
  useEffect(() => {
    if (locations.length > 0) {
      const bounds = L.latLngBounds(locations.map(l => [l.latitude, l.longitude]));
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 });
    }
  }, [locations, map]);
  return null;
};

const STATUS_LABELS = {
  en_route: 'En route',
  en_intervention: 'En intervention',
  disponible: 'Disponible',
  pause: 'En pause',
};

const STATUS_COLORS = {
  en_route: 'bg-blue-100 text-blue-700',
  en_intervention: 'bg-red-100 text-red-700',
  disponible: 'bg-emerald-100 text-emerald-700',
  pause: 'bg-amber-100 text-amber-700',
};

const GeolocationPage = ({ auth, onLogout }) => {
  const { t } = useTranslation();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState(null);

  // Default center: Vaud, Switzerland
  const defaultCenter = [46.5197, 6.6323];

  const loadLocations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/geolocation/teams`);
      setLocations(res.data || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLocations();
    const interval = setInterval(loadLocations, 30000); // refresh every 30s
    return () => clearInterval(interval);
  }, [loadLocations]);

  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
    <div className="space-y-4" data-testid="geolocation-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t('settings.geolocation')}</h1>
          <p className="text-sm text-slate-500 mt-0.5">Suivi des equipes mobiles en temps reel</p>
        </div>
        <Button variant="outline" size="sm" onClick={loadLocations} data-testid="geo-refresh-btn">
          <RefreshCw className="w-4 h-4 mr-1" /> Actualiser
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4" style={{height: 'calc(100vh - 200px)', minHeight: '500px'}}>
        {/* Map */}
        <div className="lg:col-span-3 rounded-xl overflow-hidden border border-slate-200 shadow-sm" data-testid="geo-map">
          <MapContainer
            center={defaultCenter}
            zoom={10}
            style={{ height: '100%', width: '100%', minHeight: '500px' }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {locations.length > 0 && <FitBounds locations={locations} />}
            {locations.map(loc => (
              <Marker
                key={loc.employee_id}
                position={[loc.latitude, loc.longitude]}
                icon={createTeamIcon(loc.status)}
                eventHandlers={{ click: () => setSelectedTeam(loc) }}
              >
                <Popup>
                  <div className="text-sm">
                    <p className="font-bold">{loc.employee_name}</p>
                    <p className="text-xs text-slate-500">{STATUS_LABELS[loc.status] || loc.status}</p>
                    {loc.current_task && <p className="text-xs mt-1">{loc.current_task}</p>}
                    <p className="text-xs text-slate-400 mt-1">Maj: {formatTime(loc.updated_at)}</p>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        {/* Team list */}
        <div className="space-y-2 overflow-y-auto">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
            Equipes actives ({locations.length})
          </p>

          {loading ? (
            <p className="text-sm text-slate-400 text-center py-8">{t('common.loading')}</p>
          ) : locations.length === 0 ? (
            <Card className="p-6 text-center">
              <MapPin className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">Aucune equipe active</p>
              <p className="text-xs text-slate-400 mt-1">Les positions apparaitront quand les equipes partageront leur localisation</p>
            </Card>
          ) : (
            locations.map(loc => (
              <Card
                key={loc.employee_id}
                className={`p-3 cursor-pointer transition-all hover:shadow-md ${selectedTeam?.employee_id === loc.employee_id ? 'ring-2 ring-blue-500 bg-blue-50' : ''}`}
                onClick={() => setSelectedTeam(loc)}
                data-testid={`team-${loc.employee_id}`}
              >
                <div className="flex items-start gap-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    loc.status === 'en_intervention' ? 'bg-red-100' : loc.status === 'en_route' ? 'bg-blue-100' : 'bg-emerald-100'
                  }`}>
                    <User className={`w-4 h-4 ${
                      loc.status === 'en_intervention' ? 'text-red-600' : loc.status === 'en_route' ? 'text-blue-600' : 'text-emerald-600'
                    }`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900 truncate">{loc.employee_name}</p>
                    <span className={`inline-block text-xs px-2 py-0.5 rounded-full mt-0.5 ${STATUS_COLORS[loc.status] || 'bg-slate-100 text-slate-600'}`}>
                      {STATUS_LABELS[loc.status] || loc.status}
                    </span>
                    {loc.current_task && (
                      <p className="text-xs text-slate-500 mt-1 truncate">{loc.current_task}</p>
                    )}
                    <div className="flex items-center gap-1 mt-1">
                      <Clock className="w-3 h-3 text-slate-400" />
                      <span className="text-xs text-slate-400">{formatTime(loc.updated_at)}</span>
                    </div>
                  </div>
                </div>
              </Card>
            ))
          )}

          {/* GPS coordinate display for selected team */}
          {selectedTeam && (
            <Card className="p-3 bg-slate-50 border-slate-200 mt-3">
              <div className="flex items-center gap-2 mb-1">
                <Navigation className="w-4 h-4 text-blue-600" />
                <span className="text-xs font-semibold text-slate-700">{selectedTeam.employee_name}</span>
              </div>
              <p className="text-xs text-slate-500 font-mono">
                {selectedTeam.latitude.toFixed(5)}, {selectedTeam.longitude.toFixed(5)}
              </p>
            </Card>
          )}
        </div>
      </div>
    </div>
    </AdminLayout>
  );
};

export default GeolocationPage;
